import createClient, { type Middleware } from 'openapi-fetch';
import { emitAuthEvent, isSessionKnown, markSessionKnown } from './auth-events';
import { ApiError, toApiError } from './errors';
import type { paths } from './schema';

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

/**
 * "Preview as role" (Step 15): while set, every request asks the API to apply that role
 * (read-only). Kept for this tab only (sessionStorage), so a reload stays in the preview.
 */
export interface RolePreview {
  /** A built-in role key, or "custom:<id>". */
  role: string;
  /** Whose Own / Team records fill the preview (default: you). */
  userId?: string | undefined;
}
const PREVIEW_KEY = 'tk-role-preview';
let preview: RolePreview | null = (() => {
  try {
    const raw = globalThis.sessionStorage?.getItem(PREVIEW_KEY);
    return raw ? (JSON.parse(raw) as RolePreview) : null;
  } catch {
    return null;
  }
})();
export const getRolePreview = (): RolePreview | null => preview;
export function setRolePreview(next: RolePreview | null): void {
  preview = next;
  try {
    if (next) globalThis.sessionStorage?.setItem(PREVIEW_KEY, JSON.stringify(next));
    else globalThis.sessionStorage?.removeItem(PREVIEW_KEY);
  } catch {
    // Storage blocked: the preview lasts until reload.
  }
}

/** CSRF token from the last login / me / switch-tenant response. Memory only, never storage. */
let csrfToken: string | undefined;
export const getCsrfToken = (): string | undefined => csrfToken;
export const setCsrfToken = (token: string | undefined): void => {
  csrfToken = token;
};

async function readJson(response: Response): Promise<unknown> {
  if (!response.headers.get('content-type')?.includes('json')) return undefined;
  try {
    return await response.clone().json();
  } catch {
    return undefined;
  }
}

/**
 * Runs on every request: adds the CSRF header to requests that change data, remembers new
 * CSRF tokens, and turns session-level errors into app-wide events.
 */
const sessionMiddleware: Middleware = {
  onRequest({ request }) {
    if (!SAFE_METHODS.has(request.method) && csrfToken)
      request.headers.set('X-CSRF-Token', csrfToken);
    if (preview) {
      request.headers.set('X-Preview-Role', preview.role);
      if (preview.userId) request.headers.set('X-Preview-User', preview.userId);
    }
    return request;
  },
  async onResponse({ response }) {
    const body = await readJson(response);
    if (response.ok) {
      if (
        typeof body === 'object' &&
        body !== null &&
        'csrfToken' in body &&
        typeof body.csrfToken === 'string'
      ) {
        setCsrfToken(body.csrfToken);
      }
      return response;
    }
    const code = toApiError(response, body).code;
    if (code === 'UNAUTHENTICATED' && isSessionKnown()) {
      markSessionKnown(false);
      setCsrfToken(undefined);
      emitAuthEvent('session-expired');
    } else if (code === 'TENANT_SUSPENDED') {
      emitAuthEvent('tenant-suspended');
    } else if (code === 'USER_DISABLED') {
      markSessionKnown(false);
      emitAuthEvent('user-disabled');
    } else if (code === 'ACT_AS_ENDED') {
      emitAuthEvent('act-as-ended');
    }
    return response;
  },
};

export function createApiClient(baseUrl: string = globalThis.location?.origin ?? '') {
  // Late-bound fetch so tests can replace globalThis.fetch.
  const client = createClient<paths>({ baseUrl, fetch: (request) => globalThis.fetch(request) });
  client.use(sessionMiddleware);
  return client;
}

export const api = createApiClient();

/**
 * Unwraps an openapi-fetch result: returns the data, or throws an ApiError with a code.
 * Network failures become ApiError('NETWORK').
 */
export async function call<T>(
  request: Promise<{ data?: T; error?: unknown; response: Response }>,
): Promise<T> {
  let result: { data?: T; error?: unknown; response: Response };
  try {
    result = await request;
  } catch {
    throw new ApiError(0, 'NETWORK', undefined);
  }
  if (!result.response.ok) throw toApiError(result.response, result.error);
  return result.data as T;
}

/**
 * For file uploads and downloads (not JSON): same-origin fetch with the CSRF header on requests
 * that change data. Throws an ApiError (with the server's code) on failure.
 */
export async function rawRequest(path: string, init: RequestInit = {}): Promise<Response> {
  const method = (init.method ?? 'GET').toUpperCase();
  const headers = new Headers(init.headers);
  if (!SAFE_METHODS.has(method) && csrfToken) headers.set('X-CSRF-Token', csrfToken);
  let response: Response;
  try {
    const url = new URL(path, globalThis.location?.origin ?? 'http://localhost').toString();
    response = await globalThis.fetch(new Request(url, { ...init, method, headers }));
  } catch {
    throw new ApiError(0, 'NETWORK', undefined);
  }
  if (!response.ok) throw toApiError(response, await readJson(response));
  return response;
}

/** Saves a downloaded response (e.g. a CSV) with the server's file name. */
export async function saveDownload(response: Response, fallbackName: string): Promise<void> {
  const name =
    /filename="([^"]+)"/.exec(response.headers.get('content-disposition') ?? '')?.[1] ??
    fallbackName;
  const url = URL.createObjectURL(await response.blob());
  const a = Object.assign(document.createElement('a'), { href: url, download: name });
  a.click();
  URL.revokeObjectURL(url);
}
